import {
    Reducer,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from "react";

import { navigationStateInfo, robotStatesInfo, StateInfo } from "lib/consts/robot";
import { Values } from "types/utilities";
import { IControlSource } from "arcjs/control_source/base";
import difference from "lodash/difference";
import { useFleet } from "./FleetProvider";
import makeNavigationServices, { NavigationServices } from "arcjs/services/navigation";
import { makeServiceCaller } from "arcjs/services/sources/index";
import { AnyPlainObject } from "arcjs/common/misc";

import { DummyControlSource } from "arcjs/control_source/DummyControlSource";
import { ARCInfo } from "arcjs/common/arc_devices";
import { DUMMY_SERVICE_RESPONSES } from "lib/consts/testing";
import { getSocketByARCInfo } from "lib/utils/connection";

type ARCStateDesc = typeof robotStatesInfo & typeof navigationStateInfo;

type ConnectedState = { connected: boolean };

export type ARCState = {
    [key in keyof ARCStateDesc]: ARCStateDesc[key]["default"];
} & ConnectedState;

export type ARCServices = {
    navigationService?: NavigationServices;
};

export type ARC = {
    state: ARCState;
    control: IControlSource;
    services: ARCServices;
    serialNumber: string;
    info: ARCInfo;
};

export type MultiARCs = {
    [arcSerial: string]: ARC;
};

type MultiARCsControlContextType = {
    arcs: MultiARCs;
};

export const MultiARCsControlContext = createContext<MultiARCsControlContextType>({ arcs: {} });
MultiARCsControlContext.displayName = "RobotControl";
export const useMultiARCs = () => useContext(MultiARCsControlContext);

export const useFilteredMultiARCs = ({ arcSerials = [] }: { arcSerials?: string[] } = {}) => {
    const { arcs } = useMultiARCs();
    const filteredARCs = useMemo(() => {
        return arcSerials.map((serial) => arcs[serial]).filter((a) => !!a);
    }, [arcSerials, arcs]);
    return { arcs: filteredARCs };
};

type UpdateSerialsAction = string[];

type MultiArcStates = { [arcSerial: string]: ARCState };

type UseMultiArcStates = (options: {
    arcControlSources: { [arcSerial: string]: IControlSource };
}) => {
    arcStates: MultiArcStates;
};

const subscribeFromStateInfoList = ({
    controlSource,
    stateInfoList,
    cb,
}: {
    controlSource: IControlSource;
    stateInfoList: StateInfo[];
    cb: (stateName: string) => (data: any) => any;
}) => {
    for (const stateInfo of stateInfoList) {
        for (const name in stateInfo) {
            const info = stateInfo[name];
            controlSource.subscribe(info.topic, cb(name));
        }
    }
};

type SetArcStateAction = {
    arcSerial: string;
    state: Values<{
        [key in keyof ARCState]: {
            stateName: key;
            value: ARCState[key];
        };
    }>;
};

type ARCStateReducer = Reducer<MultiArcStates, SetArcStateAction>;

type SetSingleArcStateFn = (value: Omit<SetArcStateAction, "arcSerial">) => void;

type SetupSetStateOnChangeFn = ({
    controlSource,
    setArcState,
}: {
    controlSource: IControlSource;
    setArcState: SetSingleArcStateFn;
}) => void;

const subscribeConnectionEvents: SetupSetStateOnChangeFn = ({ controlSource, setArcState }) => {
    for (const connectionEvent of ["onConnect", "onDisconnect"]) {
        controlSource[connectionEvent](() => {
            setArcState({
                state: { stateName: "connected", value: true },
            });
        });
    }
};

const fakeStateChangesIfDummy = ({ controlSource }: { controlSource: IControlSource }) => {
    if (!(controlSource instanceof DummyControlSource)) return;

    return setInterval(() => {
        controlSource.fakeMessage(navigationStateInfo.robotPose.topic, {
            // position: { x: 0, y: 0, z: 0 },
            position: { x: Math.random() * 500, y: Math.random() * 500, z: 0 },
            orientation: { x: 0, y: 0, z: 0, w: 1 },
        } as typeof navigationStateInfo.robotPose.default);
    }, 5000);
};

const subscribeStateChanges: SetupSetStateOnChangeFn = ({ controlSource, setArcState }) => {
    subscribeFromStateInfoList({
        controlSource,
        stateInfoList: [robotStatesInfo, navigationStateInfo],
        cb: (stateName) => (data) =>
            setArcState({
                // @ts-ignore
                state: { stateName, value: data },
            }),
    });
};

const useMultiARCsState: UseMultiArcStates = ({ arcControlSources }) => {
    const [arcStates, setArcsState] = useReducer<ARCStateReducer>((states, action) => {
        return {
            ...states,
            [action.arcSerial]: {
                ...states[action.arcSerial],
                [action.state.stateName]: action.state.value,
            },
        };
    }, {});

    const [, setSubscribed] = useState([]);

    useEffect(() => {
        setSubscribed((subscribed) => {
            const added = difference(Object.keys(arcControlSources), subscribed);
            const fakeChangesIntervalIds = [];
            for (const serial of added) {
                const setArcState: SetSingleArcStateFn = (value) =>
                    setArcsState({ arcSerial: serial, ...value });
                const controlSource = arcControlSources[serial];
                subscribeConnectionEvents({ controlSource, setArcState });
                subscribeStateChanges({ controlSource, setArcState });
                fakeChangesIntervalIds.push(fakeStateChangesIfDummy({ controlSource }));
            }
            return [...subscribed, ...added];
        });
    }, [arcControlSources]);

    return { arcStates };
};

const useMultiControlSources = () => {
    const { arcsInfo } = useFleet();
    const [arcControlSources, setARCSerials] = useReducer<
        Reducer<{ [arcSerial: string]: IControlSource }, UpdateSerialsAction>
    >((sources, update) => {
        const currSerials = Object.keys(sources);
        const removed = difference(currSerials, update);
        const added = difference(update, currSerials);
        for (const serial of removed) {
            if (serial in sources) {
                try {
                    sources[serial].close();
                } catch { }
                delete sources[serial];
            }
        }
        for (const serial of added) {
            const info = arcsInfo.find((info) => info.serialNumber === serial);
            if (info) sources[serial] = getSocketByARCInfo(info)!;
        }
        return { ...sources };
    }, {});

    useEffect(() => {
        setARCSerials(arcsInfo.map((arc) => arc.serialNumber));
    }, [arcsInfo]);

    return {
        arcControlSources,
    };
};

type MultiArcServices = {
    [arcSerial: string]: ARCServices;
};

type UseMultiArcServices = () => { multiArcServices: MultiArcServices };

const makeServiceCallerFromInfo = (info: ARCInfo) => {
    switch (info.controlSource) {
        case "WEBROSPROXY":
            return makeServiceCaller(info.controlSource, { serviceUrl: info.host });
        case "ROSLIBJS":
            return makeServiceCaller(info.controlSource, {} as any);
        case "FOXGLOVE":
            return makeServiceCaller(info.controlSource, {} as any);
        case "DUMMY":
            return makeServiceCaller("DUMMY", [DUMMY_SERVICE_RESPONSES]);
        default:
            throw new Error(`Unexpected control source ${info.controlSource}`);
    }
};

const useMultiArcServices: UseMultiArcServices = () => {
    const { arcsInfo } = useFleet();
    const [multiArcServices, setArcSerials] = useReducer<
        Reducer<MultiArcServices, UpdateSerialsAction>
    >((services, update) => {
        const added = difference(update, Object.keys(services));
        for (const serial of added) {
            if (!(serial in services)) {
                const info = arcsInfo.find((info) => info.serialNumber === serial);

                const caller = makeServiceCallerFromInfo(info);
                services[serial] = {
                    navigationService: makeNavigationServices(caller),
                };
            }
        }

        return { ...services };
    }, {});

    useEffect(() => {
        setArcSerials(arcsInfo.map((arc) => arc.serialNumber));
    }, [arcsInfo]);

    return { multiArcServices };
};

const makeDefaultState = (stateInfo: StateInfo) =>
    Object.entries(stateInfo).reduce(
        (result, [key, value]) => ({ ...result, [key]: value.default }),
        {}
    );

/* const useLogIfChanged = (name: string, value: any) => {
    useEffect(() => {
        console.log(`${name} changed.`, value);
    }, [name, value]);
}; */

const MultiARCsControlProvider = ({ children }) => {
    const { arcControlSources } = useMultiControlSources();
    const { arcStates } = useMultiARCsState({ arcControlSources });
    const { multiArcServices } = useMultiArcServices();
    const { arcsInfo } = useFleet();

    /*     useLogIfChanged("arcControlSources", arcControlSources);
    useLogIfChanged("arcStates", arcStates);
    useLogIfChanged("multiArcServices", multiArcServices);
    useLogIfChanged("arcsInfo", arcsInfo); */

    const arcs = useMemo(() => {
        return (arcsInfo as AnyPlainObject[]).reduce<MultiARCs>((acc, arcInfo) => {
            const serial = arcInfo.serialNumber;
            return {
                ...acc,
                [serial]: {
                    serialNumber: serial,
                    state:
                        arcStates[serial] ||
                        makeDefaultState({ ...robotStatesInfo, ...navigationStateInfo }),
                    control: arcControlSources[serial] || null,
                    services: multiArcServices[serial] || {},
                    info: arcInfo,
                },
            };
        }, {});
    }, [arcsInfo, arcStates, arcControlSources, multiArcServices]);

    // useLogIfChanged("arcs", arcs);

    return (
        <MultiARCsControlContext.Provider value={{ arcs }}>
            {children}
        </MultiARCsControlContext.Provider>
    );
};

export default MultiARCsControlProvider;
